import React, { useState } from 'react';
import { Container, Card, CardContent, TextField, Button, Box } from '@mui/material';
import HeaderHelpPage from '../HeaderHelpPage';
import GenericApi from '../../services/api';
import fileDownload from 'js-file-download';
import { styles } from "../../utils/styles";

const DownloadFromS3 = () => {
    const [url, setUrl] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const isS3Url = (inputUrl) => {
        // Regex for S3 URLs:
        // 1. Virtual-hosted: https://bucket.s3.region.amazonaws.com/key
        // 2. Path-style: https://s3.region.amazonaws.com/bucket/key
        // 3. S3 URI: s3://bucket/key
        const s3Regex = /^(https?:\/\/([^/]+\.)?s3([.-][a-z0-9-]+)?\.amazonaws\.com\/.+)|(^s3:\/\/[a-z0-9.-]+\/.+)$/i;
        return s3Regex.test(inputUrl);
    };

    const handleDownload = async () => {
        setError('');

        if (!url) {
            return;
        }

        if (!isS3Url(url)) {
            setError('Invalid link, please check the URL.');
            return;
        }

        setLoading(true);
        try {
            const response = await GenericApi.post('/admins/download_from_s3/download/', { url }, {
                responseType: 'blob',
            });

            if (response.status === 200) {
                // Try to get filename from Content-Disposition header
                let fileName = 'download';
                const contentDisposition = response.headers['content-disposition'];
                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                    if (fileNameMatch && fileNameMatch.length === 2) {
                        fileName = fileNameMatch[1];
                    }
                } else {
                    // Fallback: try to guess filename from URL
                    try {
                        const urlParts = url.split('/');
                        const extractedName = urlParts[urlParts.length - 1];
                        if (extractedName) fileName = extractedName;
                    } catch (e) {
                        console.error('Error parsing URL for filename', e);
                    }
                }

                fileDownload(response.data, fileName);
            } else {
                // Try to read the blob as text to show error message if possible
                if (response.data instanceof Blob) {
                    const text = await response.data.text();
                    try {
                        const json = JSON.parse(text);
                        setError(json.detail || 'Unable to download file.');
                    } catch (e) {
                        setError('Unable to locate the document, please check the URL.');
                    }
                } else {
                    setError('Unable to locate the document, please check the URL.');
                }
            }
        } catch (err) {
            console.error(err);
            let errorMessage = 'Unable to download file.';

            // If response is a blob (which it is because of responseType), we need to read it
            if (err.response && err.response.data instanceof Blob) {
                const text = await err.response.data.text();
                try {
                    const json = JSON.parse(text);
                    if (json.detail) {
                        errorMessage = json.detail;
                        if (json.key) {
                            console.log("Failed key:", json.key);
                        }
                    }
                } catch (e) { /* ignore parse error */ }
            } else if (err.response && err.response.data && err.response.data.detail) {
                errorMessage = err.response.data.detail;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container>
            <HeaderHelpPage title="Download from S3" description="Download files directly from S3 URLs" />
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <TextField
                            label="S3 URL"
                            fullWidth
                            size="small"
                            color="secondary"
                            value={url}
                            onChange={(e) => setUrl(e.target.value.trim())}
                            error={!!error}
                            helperText={error}
                            disabled={loading}
                            placeholder="https://bucket-name.s3.region.amazonaws.com/file or s3://bucket/file"
                        />
                        <Button
                            variant="contained"
                            onClick={handleDownload}
                            disabled={loading || !url}
                            sx={{ ...styles.customButton }}
                        >
                            {loading ? 'Downloading...' : 'Download'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Container>
    );
};

export default DownloadFromS3;
